/**
 * Propostas (`/propostas`) — histórico compartilhado de toda a equipe (não
 * "minhas propostas"). Tabela paginada com busca (código/cliente/CNPJ ou CPF),
 * estado, vendedor, status e período. `GET /users` é só para administradores
 * — para quem não é admin, degrada para "sem filtro de vendedor" em vez de
 * mostrar erro (ver docs/API.md).
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { get } from '@/lib/api';
import { fmtDate, PROPOSAL_STATUS_LABEL } from '@/lib/format';
import type { ProposalListRow, ProposalStatus, StateTax, User } from '@/lib/types';
import { useProposals } from '@/hooks/usePropostas';
import { PageHeader } from '@/components/PageHeader';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { EmptyState } from '@/components/EmptyState';
import { Badge, type BadgeVariant } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Field } from '@/components/Field';
import { Money } from '@/components/Money';
import './Propostas.css';

const PER_PAGE = 25;

const STATUS_OPTIONS: ProposalStatus[] = ['rascunho', 'emitida', 'aceita', 'recusada', 'cancelada'];

function statusVariant(status: ProposalStatus): BadgeVariant {
  switch (status) {
    case 'rascunho':
      return 'neutral';
    case 'emitida':
      return 'accent';
    case 'aceita':
      return 'positive';
    case 'recusada':
      return 'alert';
    case 'cancelada':
      return 'attention';
    default:
      return 'neutral';
  }
}

export function Propostas() {
  const navigate = useNavigate();

  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [uf, setUf] = useState('');
  const [userId, setUserId] = useState<number | ''>('');
  const [status, setStatus] = useState<ProposalStatus | ''>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => window.clearTimeout(t);
  }, [q]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQ, uf, userId, status, from, to]);

  const statesQuery = useQuery({
    queryKey: ['states'],
    queryFn: () => get<{ data: StateTax[] }>('/states').then((r) => r.data),
    staleTime: 5 * 60_000,
  });

  // `GET /users` é só de admin — para quem não é, a lista fica vazia e o filtro some (sem toast de erro).
  const usersQuery = useQuery({
    queryKey: ['users-for-proposals-filter'],
    queryFn: () => get<{ data: User[] }>('/users').then((r) => r.data),
    retry: false,
    staleTime: 5 * 60_000,
  });
  const vendors = usersQuery.data ?? [];

  const proposalsQuery = useProposals({
    q: debouncedQ || undefined,
    uf: uf || undefined,
    status: status || undefined,
    userId: userId === '' ? undefined : userId,
    from: from || undefined,
    to: to || undefined,
    page,
    perPage: PER_PAGE,
  });

  const rows = proposalsQuery.data?.data ?? [];
  const total = proposalsQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const hasFilters = debouncedQ !== '' || uf !== '' || userId !== '' || status !== '' || from !== '' || to !== '';

  function clearFilters() {
    setQ('');
    setUf('');
    setUserId('');
    setStatus('');
    setFrom('');
    setTo('');
  }

  const columns: DataTableColumn<ProposalListRow>[] = [
    { key: 'code', header: 'Código', render: (row) => <span className="mono">{row.code}</span> },
    { key: 'createdAt', header: 'Data', render: (row) => fmtDate(row.createdAt) },
    { key: 'cliente', header: 'Cliente', render: (row) => row.clientRazaoSocial },
    { key: 'uf', header: 'UF', render: (row) => row.estadoUf },
    { key: 'vendedor', header: 'Vendedor', render: (row) => row.sellerName || <span className="faint">—</span> },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <Badge variant={statusVariant(row.status)}>{PROPOSAL_STATUS_LABEL[row.status]}</Badge>,
    },
    { key: 'total', header: 'Total', numeric: true, render: (row) => <Money value={row.totalGeral} /> },
  ];

  return (
    <>
      <PageHeader
        title="Propostas"
        meta="Histórico compartilhado de toda a equipe comercial — não só as suas propostas."
        actions={
          <Link className="btn btn-primary" to="/nova">
            Nova proposta
          </Link>
        }
      />

      <div className="toolbar propostas-filters">
        <Field
          label="Buscar"
          wrapperClassName="propostas-filters__search"
          placeholder="Código, cliente ou CNPJ/CPF…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <Field as="select" label="Estado" value={uf} onChange={(e) => setUf(e.target.value)}>
          <option value="">Todos os estados</option>
          {(statesQuery.data ?? []).map((s) => (
            <option key={s.uf} value={s.uf}>
              {s.nome}
            </option>
          ))}
        </Field>
        {vendors.length > 0 ? (
          <Field
            as="select"
            label="Vendedor"
            value={userId}
            onChange={(e) => setUserId(e.target.value === '' ? '' : Number(e.target.value))}
          >
            <option value="">Todos</option>
            {vendors.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </Field>
        ) : null}
        <Field
          as="select"
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value as ProposalStatus | '')}
        >
          <option value="">Todos</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {PROPOSAL_STATUS_LABEL[s]}
            </option>
          ))}
        </Field>
        <Field label="De" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        <Field label="Até" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        {hasFilters ? (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            Limpar filtros
          </Button>
        ) : null}
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        loading={proposalsQuery.isLoading}
        onRowClick={(row) => navigate(`/propostas/${row.id}`)}
        empty={
          hasFilters ? (
            <EmptyState
              title="Nenhuma proposta encontrada"
              description="Tente outro termo de busca ou outro filtro."
              action={
                <Button variant="ghost" onClick={clearFilters}>
                  Limpar filtros
                </Button>
              }
            />
          ) : (
            <EmptyState
              title="Nenhuma proposta ainda."
              description="A primeira leva menos de dois minutos."
              action={
                <Link className="btn btn-primary" to="/nova">
                  Nova proposta
                </Link>
              }
            />
          )
        }
      />

      {total > 0 && totalPages > 1 ? (
        <div className="row row-between propostas-pagination">
          <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Anterior
          </Button>
          <span className="muted propostas-pagination__info">
            Página {page} de {totalPages} · {total} {total === 1 ? 'proposta' : 'propostas'}
          </span>
          <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            Próxima
          </Button>
        </div>
      ) : null}
    </>
  );
}
